// Locale-aware formatting for dates, money and counts. Every page should go
// through these helpers so the same value always renders the same way.

import { t } from './i18n.jsx';

const LOCALES = { nl: 'nl-NL', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' };

export function localeFor(lang) {
  return LOCALES[lang] || LOCALES.nl;
}

// A date-only string ("2026-10-09") is a calendar date, not an instant.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
// An ISO datetime without "Z" or "+hh:mm". The API stores UTC, and older
// responses omit the offset, so such values are read as UTC.
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

export function parseApiDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const s = String(value).trim();
  let d;
  if (DATE_ONLY.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    d = new Date(y, m - 1, day);
  } else if (NAIVE_DATETIME.test(s)) {
    d = new Date(s.replace(' ', 'T') + 'Z');
  } else {
    d = new Date(s);
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value, lang = 'nl') {
  const d = parseApiDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
}

export function formatDateTime(value, lang = 'nl') {
  const d = parseApiDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatTime(value, lang = 'nl') {
  const d = parseApiDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(localeFor(lang), { hour: '2-digit', minute: '2-digit' }).format(d);
}

export function relativeTime(value, lang = 'nl') {
  const d = parseApiDate(value);
  if (!d) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 0 || diff >= 86400 * 7) return formatDate(d, lang);
  if (diff < 60) return t(lang, 'format.justNow');
  const rtf = new Intl.RelativeTimeFormat(localeFor(lang), { numeric: 'auto', style: 'short' });
  if (diff < 3600) return rtf.format(-Math.floor(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.floor(diff / 3600), 'hour');
  return rtf.format(-Math.floor(diff / 86400), 'day');
}

// Whole days from now until the given date (negative when in the past).
export function daysUntil(value) {
  const d = parseApiDate(value);
  if (!d) return null;
  return Math.round((d.getTime() - Date.now()) / 86400000);
}

export function formatMoney(amount, lang = 'nl', currency = 'EUR') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat(localeFor(lang), { style: 'currency', currency }).format(n);
}

// "€ 4,95 /mnd" — a recurring monthly price.
export function formatMonthly(amount, lang = 'nl') {
  return t(lang, 'format.perMonth', { amount: formatMoney(amount, lang) });
}

export function formatNumber(n, lang = 'nl', opts) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return new Intl.NumberFormat(localeFor(lang), opts).format(v);
}

// plural(1, 'domein', 'domeinen') → "1 domein"
export function plural(count, one, many) {
  return `${count} ${count === 1 ? one : many}`;
}
